import React, { useState, useEffect } from 'react';
import { Button } from '../../ui/button';
import { Loader2, ExternalLink, FileText, BookOpen } from 'lucide-react';
import { useModuleDetailsQuery, CourseModule } from '../../../api/studentCoursesApi';
import ModuleCompleteButton from '../common/ModuleCompleteButton';
import { tokenService } from '../../../services/TokenService';
import './PageModuleStyles.css';

interface PageModuleContentProps {
  contextId: string;
  moduleId: string;
  module: CourseModule | null;
  headerColor?: string;
}

const PageModuleContent: React.FC<PageModuleContentProps> = ({ contextId, moduleId, module, headerColor }) => {
  const token = tokenService.getToken();
  const [pageContent, setPageContent] = useState<string>('');
  const [isFetchingContent, setIsFetchingContent] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const { data: details, isLoading, error } = useModuleDetailsQuery(
    contextId,
    moduleId,
    'page',
    token,
    {
      enabled: !!contextId && !!moduleId,
    }
  );

  // Fetch HTML content
  useEffect(() => {
    const fetchHtmlContent = async () => {
      // First try to get content from API response
      if (details) {
        const apiContent = details.intro || details.description || details.content || '';
        if (apiContent) {
          setPageContent(apiContent);
          return;
        }
      }

      // If no content from API, try to fetch from index.html
      if (module?.contents && module.contents.length > 0) {
        const htmlFile = module.contents.find(c => c.filename === 'index.html');
        if (htmlFile?.fileurl) {
          setIsFetchingContent(true);
          setFetchError(null);
          
          try {
            const response = await fetch(htmlFile.fileurl);
            if (!response.ok) {
              throw new Error(`Failed to fetch content: ${response.statusText}`);
            }
            const html = await response.text();
            setPageContent(html);
          } catch (err) {
            console.error('Error fetching HTML content:', err);
            setFetchError(err instanceof Error ? err.message : 'Failed to load content');
          } finally {
            setIsFetchingContent(false);
          }
        }
      }
    };

    if (details || module?.contents) {
      fetchHtmlContent();
    }
  }, [details, module?.contents]);

  const handleOpenInMoodle = () => {
    if (module?.url) {
      window.open(module.url, '_blank');
    }
  };

  // Loading State
  if (isLoading || isFetchingContent) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-12">
        <div className="flex flex-col items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500 mb-3" />
          <span className="text-gray-600">
            {isFetchingContent ? 'Loading content...' : 'Loading...'}
          </span>
        </div>
      </div>
    );
  }

  // Error State
  if (error || fetchError) {
    return (
      <div className="bg-white rounded-lg border border-red-200 p-6">
        <div className="flex items-start gap-3">
          <div className="shrink-0">
            <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
              <FileText className="h-5 w-5 text-red-600" />
            </div>
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-medium text-red-800 mb-1">
              Failed to load page content
            </h3>
            <p className="text-sm text-red-700">
              {fetchError || (error instanceof Error ? error.message : 'An unknown error occurred')}
            </p>
            {module?.url && (
              <Button
                onClick={handleOpenInMoodle}
                variant="outline"
                size="sm"
                className="mt-3"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Open in Moodle
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Page Content
  if (pageContent) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {/* ── Header ── */}
        <div className="px-6 py-5 overflow-hidden" style={{ background: '#F9FAFB', borderTop: `4px solid ${headerColor || '#374151'}` }}>
          <div className="flex items-start gap-4">
            <div className="rounded-lg p-2.5 shrink-0" style={{ background: headerColor ? `${headerColor}1A` : '#E5E7EB' }}>
              <BookOpen className="h-6 w-6" style={{ color: headerColor || '#374151' }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-bold uppercase tracking-widest text-gray-500 mb-1">Page</p>
              <h2 className="text-xl font-bold leading-snug text-gray-900">{module?.name || 'Page'}</h2>
            </div>
          </div>
        </div>

        {/* ── Content ── */}
        <div className="px-6 py-5 max-h-[calc(100vh-11rem)] overflow-y-auto">
          <div
            className="moodle-content"
            dangerouslySetInnerHTML={{ __html: pageContent }}
          />
        </div>

        {/* ── Footer ── */}
        <div className="sticky bottom-0 z-10 border-t border-gray-200 px-4 py-3 bg-gray-50 flex justify-end">
          <ModuleCompleteButton contextId={contextId} moduleId={moduleId} variant="footer" />
        </div>
      </div>
    );
  }

  // No Content State
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-12">
      <div className="text-center">
        <FileText className="h-12 w-12 text-gray-400 mx-auto mb-3" />
        <p className="text-gray-600 mb-4">No content available for this page</p>
        {module?.url && (
          <Button onClick={handleOpenInMoodle} variant="outline">
            <ExternalLink className="h-4 w-4 mr-2" />
            Open in Moodle
          </Button>
        )}
      </div>
    </div>
  );
};

export default PageModuleContent;
